/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { BehaviorSubject, EMPTY, Observable, combineLatest, merge, of } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ComponentEffects, logCatchError } from '@mhe/reader/common';
import {
  Bonsai,
  BulkTopicsResponseRow,
  SpineItem,
  Tag,
  TagLocation,
  TagNode,
  TagType,
  TocItem,
} from '@mhe/reader/models';
import { FeatureTogglesService } from '@mhe/reader/features';

import { MediatorUtils } from './mediator-utils';
import { DFAService } from '@mhe/reader/core/dfa-api/dfa.service';
import {
  TopicsStore,
  buildTopicTocReverseMap,
  summarizeTopicTocReverseMap,
  TopicTocReverseMapEntry,
} from '@mhe/reader/components/topics/state';
import * as topicsActions from '@mhe/reader/components/topics/state/topics.actions';
import { EpubLibCFIService } from '@mhe/reader/features/annotation';
import { NavigationStore } from '@mhe/reader/components/navigation';
import * as navigationActions from '@mhe/reader/components/navigation/state/navigation.actions';
import { TocStore } from '@mhe/reader/components/toc';
import * as tocActions from '@mhe/reader/components/toc';
import {
  ReaderConfigStore,
  ReaderStore,
} from '@mhe/reader/components/reader/state';
import { ElasticSearchService } from './elastic-search.service';
import { DfaSearchNode } from '../components/elastic-search/elastic-search.model';
import { TopicMetadataStore, TopicResolutionHints } from '../components/topics/state/topic-metadata.store';
import * as readerActions from '@mhe/reader/components/reader/state/reader.actions';

function isDebugMode(): boolean {
  return typeof window !== 'undefined' && localStorage.getItem('readerx_debug') === 'true';
}

function emitDiagnostics(label: string, data: any): void {
  if (isDebugMode()) {
    // console.log(`[TopicsMediator] ${label}`, data);
  }
}

function calculateAverageCandidateScore(entries: TopicTocReverseMapEntry[]): number {
  if (!entries.length) return 0;
  const totalScore = entries.reduce((sum, entry) => sum + (entry.bestCandidate?.score || 0), 0);
  return Math.round((totalScore / entries.length) * 100) / 100;
}

@Injectable()
export class TopicsMediator extends ComponentEffects {
  private readonly topicsActions$ = this.topicsStore.actions$;
  private readonly navigationActions$ = this.navigationStore.actions$;
  private readonly readerActions$ = this.readerStore.actions$;
  private readonly hydratedSubjectIds = new Set<string>();

  /** Carries the latest navigation context hints so syncTopicMetadata$ can pass them to the resolver. */
  private readonly currentNavigationHints$ = new BehaviorSubject<TopicResolutionHints>({});

  constructor(
    private readonly readerConfigStore: ReaderConfigStore,
    private readonly dfaService: DFAService,
    private readonly topicsStore: TopicsStore,
    private readonly readerStore: ReaderStore,
    private readonly navigationStore: NavigationStore,
    private readonly mediatorUtils: MediatorUtils,
    private readonly epubLibCfiService: EpubLibCFIService,
    private readonly tocStore: TocStore,
    private readonly elasticSearchService: ElasticSearchService,
    private readonly topicMetadataStore: TopicMetadataStore,
    private readonly featureTogglesService: FeatureTogglesService,
  ) {
    super();
  }

  readonly setSubjects = combineLatest([
    this.readerConfigStore.topicsEndpoint$,
    this.readerConfigStore.idmToken$,
  ]).pipe(
    filter(([_, token]) => Boolean(token)),
    switchMap(([topicsEndpoint]) => {
      return this.dfaService.requestSubjectsOrTags(topicsEndpoint).pipe(
        take(1),
        tap((tags) => {
          this.topicsStore.setLoading(false);
          this.topicsStore.setSubjects(tags);
        }),
      );
    }),
    withLatestFrom(
      this.readerConfigStore.dfaCategoryUUID$,
      this.readerConfigStore.dfaTopicUUID$,
      this.readerConfigStore.subjectTopicsEndpoint$,
      this.readerConfigStore.bulkTopicsEndpoint$,
      this.featureTogglesService.bulkHytHydration$,
    ),
    switchMap(([
      _,
      dfaCategoryUUID,
      dfaTopicUUID,
      subjectTopicsEndpoint,
      bulkTopicsEndpoint,
      bulkHydrationEnabled,
    ]) => {
      const shouldUseBulkHydration =
        bulkHydrationEnabled === true &&
        Boolean(bulkTopicsEndpoint);

      const hydration$ = shouldUseBulkHydration
        ? this.hydrateFromBulkEndpoint(bulkTopicsEndpoint)
        : of(null);

      return hydration$.pipe(
        switchMap(() => {
          const shouldRedirect =
            dfaCategoryUUID !== undefined && dfaTopicUUID !== undefined;

          if (shouldRedirect && shouldUseBulkHydration) {
            this.topicsStore.dispatch(topicsActions.navigateToTopic());
            return EMPTY;
          }

          if (shouldRedirect) {
            return this.getDfaLocations(
              subjectTopicsEndpoint,
              dfaCategoryUUID,
              shouldRedirect,
            ).pipe(take(1));
          }
          return EMPTY;
        }),
      );
    }),
    logCatchError('setSubjects', false, this.topicsStore.setErrorLoadingSubjects),
  );

  private hydrateFromBulkEndpoint(
    bulkTopicsEndpoint: string,
  ): Observable<unknown> {
    this.topicsStore.setLoading(true);

    return this.dfaService.requestBulkTopics(bulkTopicsEndpoint).pipe(
      take(1),
      withLatestFrom(this.topicsStore.topicsTree$),
      tap(([bulkRows, topicsTree]) => {
        const subjectIds = this.getUnhydratedSubjectIds(topicsTree);
        const rowsByParentUuid = this.createBulkRowsMap(bulkRows);
        const missingSubjectIds = subjectIds.filter(
          (subjectId) => !rowsByParentUuid.has(subjectId),
        );

        if (missingSubjectIds.length > 0) {
          throw new Error(
            `Bulk HYT endpoint returned incomplete payload. Missing subjects: ${missingSubjectIds.join(',')}`,
          );
        }

        subjectIds.forEach((subjectId) => {
          this.topicsStore.setSubjectLoadingState({ subjectId, loading: true });
        });

        subjectIds.forEach((subjectId) => {
          const tags = rowsByParentUuid.get(subjectId) || [];
          this.sortDfaTags(tags);
          this.hydratedSubjectIds.add(subjectId);
          this.topicsStore.setTopics({ subjectId, tags });
        });
      }),
      catchError((error) => {
        console.error('Bulk HYT hydration failed. Falling back to lazy hydration.', error);

        this.topicsStore.topicsTree$
          .pipe(take(1))
          .subscribe((topicsTree) => {
            const subjectIds = this.getUnhydratedSubjectIds(topicsTree);
            subjectIds.forEach((subjectId) => {
              this.topicsStore.setSubjectLoadingState({ subjectId, loading: false });
            });
          });

        return of(null);
      }),
      finalize(() => {
        this.topicsStore.setLoading(false);
      }),
    );
  }

  private createBulkRowsMap(
    bulkRows: BulkTopicsResponseRow[],
  ): Map<string, Tag[]> {
    return bulkRows.reduce((mapByParentUuid, row) => {
      mapByParentUuid.set(row.parentUUID, row.tags || []);
      return mapByParentUuid;
    }, new Map<string, Tag[]>());
  }

  readonly init$ = this.effect(() => {
    return merge(this.topicsActions$).pipe(
      ofType(topicsActions.init),
      withLatestFrom(
        this.readerConfigStore.dfaCategoryUUID$,
        this.readerConfigStore.dfaTopicUUID$,
        this.topicsStore.initialSubjectId$,
      ),
      filter(
        ([, categoryId, subjectId, initialSubjectId]) =>
          !!categoryId && !!subjectId && initialSubjectId === undefined,
      ),
      take(1),
      tap(([, categoryId, subjectId]) => {
        this.topicsStore.updateExpandedSubjects({ id: categoryId as string });
        this.topicsStore.setInitialSubjectId(subjectId);
      }),
      logCatchError('init$'),
    );
  });

  readonly navigateToTopic$ = this.effect(() => {
    return merge(this.topicsActions$).pipe(
      ofType(topicsActions.navigateToTopic),
      withLatestFrom(
        this.topicsStore.topicsTree$,
        this.readerConfigStore.dfaTopicUUID$,
        this.readerConfigStore.cfi$,
      ),
      filter(([, topicsTree]) => Object.values(topicsTree).length > 0),
      tap(([, topicsTree, topicId, cfi]) => {
        const tagNode = topicsTree[topicId as string] as TagNode;
        if (tagNode) {
          this.topicsStore.setCurrentTopicNode({
            node: tagNode,
          });
          const location = tagNode.locations?.find(
            (location) => location.cfi === cfi,
          ) as TagLocation;
          this.topicsStore.dispatch(topicsActions.setTopicInToc({ location }));
        } else {
          console.warn(
            ` - unable to navigate user since there's no tag featuring topicId such as '${topicId}' into topics tree`,
          );
        }
      }),
      logCatchError('navigateToTopic$'),
    );
  });

  getDfaLocations = (
    subjectTopicsEndpoint,
    subjectId,
    shouldRedirect?,
  ): Observable<any> => {
    this.hydratedSubjectIds.add(subjectId);

    return this.dfaService.requestSubjectsOrTags(subjectTopicsEndpoint, subjectId).pipe(
      take(1),
      tap((tags) => {
        this.sortDfaTags(tags);
        this.topicsStore.setTopics({ subjectId, tags });
        if (shouldRedirect) { this.topicsStore.dispatch(topicsActions.navigateToTopic()); }
      }),
      catchError(() => {
        this.topicsStore.setSubjectLoadingState({ subjectId, loading: false });
        return EMPTY;
      }),
    );
  };

  /**
   * Captures the navigation hint (anchor UUID + spine index) from every spine change
   * so that subsequent metadata resolution can use them.
   */
  readonly captureNavigationHints$ = this.effect(() =>
    this.readerActions$.pipe(
      ofType(readerActions.setSpineItem),
      tap(({ spineItem, hash }) => {
        const anchorUuid = hash ? hash.replace(/^#/, '') : undefined;
        this.currentNavigationHints$.next({
          targetAnchorUuid: anchorUuid,
          currentSpineIndex: (spineItem).index,
        });
      }),
      logCatchError('captureNavigationHints$'),
    ),
  );

  readonly rebuildTopicTocReverseMap$ = this.effect(() =>
    combineLatest([
      this.topicsStore.topicsTree$,
      this.readerStore.toc$,
    ]).pipe(
      filter(([topicsTree, toc]) => Object.keys(topicsTree || {}).length > 0 && Boolean(toc?.length)),
      map(([topicsTree, toc]) => {
        const startTime = performance.now();
        return { reverseMap: buildTopicTocReverseMap(topicsTree as Record<string, TagNode>, toc), startTime };
      }),
      tap(({ reverseMap, startTime }) => {
        const rebuildTime = Math.round((performance.now() - startTime) * 100) / 100;
        this.topicsStore.setTopicTocReverseMap(reverseMap);
        emitDiagnostics('topicTocReverseMap rebuilt', {
          entries: reverseMap.entries.length,
          rebuildTimeMs: rebuildTime,
          summary: summarizeTopicTocReverseMap(reverseMap),
          avgScore: calculateAverageCandidateScore(reverseMap.entries),
        });
      }),
      logCatchError('rebuildTopicTocReverseMap$'),
    ),
  );

  readonly syncTopicMetadata$ = this.effect(() => {
    return combineLatest([
      this.topicsStore.topicsTree$,
      this.topicsStore.topicTocReverseMap$,
      this.topicsStore.selectedLocation$,
      this.topicsStore.currentSpineId$,
      this.readerConfigStore.subjectTopicsEndpoint$,
      this.currentNavigationHints$,
      this.tocStore.activeTocItem$.pipe(startWith(undefined)),
    ]).pipe(
      filter(([, , , currentSpineId]) => Boolean(currentSpineId)),
      map(([
        topicsTree,
        topicTocReverseMap,
        selectedLocation,
        currentSpineId,
        subjectTopicsEndpoint,
        hints,
        activeTocItem,
      ]) => {
        const tocAnchorUuid =
          activeTocItem?.id && activeTocItem.id !== currentSpineId
            ? activeTocItem.id
            : undefined;

        const mergedHints: TopicResolutionHints = {
          ...hints,
          targetAnchorUuid: hints.targetAnchorUuid || tocAnchorUuid,
          targetTitle: hints.targetTitle || activeTocItem?.label,
          targetSpineId: hints.targetSpineId || activeTocItem?.spineItem?.id,
        };

        return {
          topicsTree,
          selectedLocation,
          currentSpineId,
          subjectTopicsEndpoint,
          hints: mergedHints,
          metadata: this.topicMetadataStore.updateFromNavigationContext(
            topicsTree,
            topicTocReverseMap,
            selectedLocation,
            currentSpineId,
            mergedHints,
          ),
        };
      }),
      // Eager hydration disabled intentionally.
      // Metadata/reverse-map is now built only from already-hydrated HYT subjects
      // loaded through direct HYT UI interaction (expand/select).
      tap(({ metadata, hints, currentSpineId }) => {
        if (!metadata) {
          emitDiagnostics('syncTopicMetadata$ unmatched (hydration disabled)', {
            currentSpineId,
            hints,
          });
        }
      }),
      logCatchError('syncTopicMetadata$'),
    );
  });

  readonly getLocations$ = this.effect(() => {
    return merge(this.topicsActions$).pipe(
      ofType(topicsActions.getLocations),
      map((subject) => subject.uuid),
      withLatestFrom(
        this.readerConfigStore.subjectTopicsEndpoint$,
      ),
      switchMap(([subjectId, subjectTopicsEndpoint]) => {
        return this.getDfaLocations(subjectTopicsEndpoint, subjectId);
      }),
      map((t) => t),
      logCatchError('getLocations$'),
    );
  });

  readonly selectInternalLocation$ = this.effect(() =>
    this.topicsActions$.pipe(
      ofType(topicsActions.selectInternalLocation),
      map((action) => action.location),
      tap((location) => {
        this.topicsStore.dispatch(
          topicsActions.setSelectedLocation({ location }),
        );
        this.topicsStore.dispatch(topicsActions.setTopicInToc({ location }));
      }),
      tap(() => this.mediatorUtils.leftDrawer(false)),
      logCatchError('selectInternalLocation$'),
    ),
  );

  readonly setCurrentSpineItem$ = this.effect(() =>
    this.navigationActions$.pipe(
      ofType(navigationActions.navigateTo),
      withLatestFrom(this.readerStore.spineItem$),
      filter(([, spineItem]) => spineItem !== undefined),
      tap(([, spineItem]) => {
        this.topicsStore.setCurrentSpineId((spineItem as SpineItem).id);
        this.topicsStore.setSelectedLocation(null);
        emitDiagnostics('setCurrentSpineItem$', {
          newSpineId: (spineItem as SpineItem).id,
          newSpineIndex: (spineItem as SpineItem).index,
        });
      }),
      logCatchError('setCurrentSpineItem$'),
    ),
  );

  readonly navigateFromTocSelection$ = this.effect(() =>
    this.tocStore.activeTocItem$.pipe(
      filter((tocItem) => Boolean(tocItem)),
      withLatestFrom(
        this.topicsStore.topicsTree$,
        this.topicsStore.topicTocReverseMap$,
        this.topicsStore.selectedLocation$,
        this.topicsStore.currentSpineId$,
      ),
      tap(([tocItem, topicsTree, reverseMap, selectedLocation, currentSpineId]) => {
        if (!tocItem || !reverseMap || !topicsTree) {
          return;
        }

        // Try to find a matching topic via the reverse map
        const reverseMapEntry = reverseMap.entries.find(
          (entry) => entry.bestCandidate?.tocId === tocItem.id,
        );

        if (reverseMapEntry) {
          // Found a direct TOC ID match, navigate to this topic
          const topicNode = topicsTree[reverseMapEntry.topicId];
          if (topicNode) {
            this.topicsStore.setCurrentTopicNode({ node: topicNode });
            if (reverseMapEntry.location) {
              this.topicsStore.setSelectedLocation(reverseMapEntry.location);
            }
            emitDiagnostics('navigateFromTocSelection$', {
              tocItemId: tocItem.id,
              tocItemLabel: tocItem.label,
              matchedTopicId: reverseMapEntry.topicId,
              matchedTopicTitle: reverseMapEntry.topicTitle,
              matchType: reverseMapEntry.bestCandidate?.matchType,
            });
          }
        }
      }),
      logCatchError('navigateFromTocSelection$'),
    ),
  );

  readonly setSelectedLocation$ = this.effect(() => {
    return this.topicsActions$.pipe(
      ofType(topicsActions.setSelectedLocation),
      map((action) => action.location),
      withLatestFrom(this.readerStore.spineItem$),
      tap(([location, spineItem]) => {
        this.topicsStore.setCurrentSpineId((spineItem as SpineItem).id);
        this.topicsStore.setSelectedLocation(location);
        this.topicsStore.dispatch(topicsActions.setTopicInToc({ location }));
        emitDiagnostics('setSelectedLocation$', {
          selectedCfi: location?.cfi,
          selectedTitle: location?.title,
          currentSpineId: (spineItem as SpineItem).id,
        });
      }),
      logCatchError('setSelectedLocation$'),
    );
  });

  readonly setTocItem = this.effect(() => {
    return this.topicsActions$.pipe(
      ofType(topicsActions.setTopicInToc),
      map((action) => action.location),
      withLatestFrom(this.readerStore.flatToc$),
      tap(([location, flatToc]) => {
        //  Update TOC with topic location
        const nodeSpineId = this.epubLibCfiService.getSpineId(location?.cfi);
        const tocElement = Object.values(flatToc).find(
          (flatTocItem: { id }) => flatTocItem?.id === nodeSpineId,
        );
        if (!tocElement) {
          console.warn(
            'There is no ToC element for the current CFI:',
            location?.cfi,
          );
          return;
        }
        this.tocStore.dispatch(
          tocActions.setActiveTocItem({ tocItem: tocElement as TocItem }),
        );
      }),
    );
  });

  readonly navigateByDfaSearchLaunch = this.effect(() => {
    return this.topicsActions$.pipe(
      ofType(topicsActions.dfaSearchLaunch),
      map((action) => action.params),
      withLatestFrom(this.readerStore.flatToc$),
      tap(([params, toc]) => {
        const item = Object.values(toc).find(
          (item: TocItem) => item.id === params.spineID,
        ) as TocItem;
        if (item) {
          const node: DfaSearchNode = {
            spineIndex: item.spinePos,
            index: params.index,
            innerHTML: decodeURIComponent(params.text) || '',
          };
          this.elasticSearchService.handleSearchResultClicked(node);
        } else {
          this.mediatorUtils.navigate(0);
        }
      }),
      logCatchError('navigateByDfaSearchLaunch', false),
    );
  });

  // Sort DFA data returned from API
  sortDfaTags(tags: Tag[]): void {
    tags.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }

  private getUnhydratedSubjectIds(
    topicsTree: Record<string, { childIds?: string[], type?: TagType, loading?: boolean }>,
  ): string[] {
    const rootNode = topicsTree?.[Bonsai.RootNode];
    const subjectIds = rootNode?.childIds || [];

    return subjectIds.filter((subjectId: string) => {
      const subjectNode = topicsTree?.[subjectId];

      return Boolean(
        subjectNode?.type === TagType.SUBJECT &&
        !subjectNode.loading &&
        !this.hydratedSubjectIds.has(subjectId),
      );
    });
  }

  private hydrateSubjects(
    subjectTopicsEndpoint: string,
    subjectIds: string[],
  ): Observable<unknown> {
    if (!subjectTopicsEndpoint || subjectIds.length === 0) {
      return EMPTY;
    }

    subjectIds.forEach((subjectId) => {
      this.topicsStore.setSubjectLoadingState({ subjectId, loading: true });
    });

    return combineLatest(subjectIds.map((subjectId) => this.getDfaLocations(subjectTopicsEndpoint, subjectId)));
  }
}
